import { qfRestGet } from './qfClient';

/**
 * Market breadth — how the whole tape moved today, bucketed by size of move.
 *
 * **LIVE** — QuantForge `GET /v1/steamroom/tape-breadth`, no parameters.
 *
 * ### The shape of the migration
 *
 * The reference implementation did this in the browser: four Financial
 * Modeling Prep requests (three exchange dumps plus a screener), ~23,000 rows
 * filtered and banded client-side. That is the wrong place for it — it ships
 * a megabyte of quotes to compute nine integers, and every open tab repeats
 * the work.
 *
 * QuantForge computes it and returns the finished object, cached ~60s
 * single-flight: one full-tape scan per minute however many clients poll, and
 * no vendor call on the request path at all.
 *
 * ### ⚠️ Their universe is not our universe, and that is expected
 *
 * QF serves **their own tape** — every equity symbol they ingest — which is a
 * different population from the FMP-derived list the rules below describe.
 * They exclude cash indices (SPX, NDX, RUT, VIX; by explicit list, so tradable
 * ETFs like VIXY and SPXL stay in) and a sub-$1 prior close, but they
 * deliberately did **not** reproduce the warrant/right/unit/preferred rules —
 * those are written against FMP security-type fields QF does not carry, and
 * reverse-engineering another vendor's taxonomy would drift silently.
 *
 * So expect `universe` to read somewhat **higher** than the ~6,560 below. The
 * bands are ratios, so the *shape* of the day should still agree closely even
 * where the counts do not. `universe` is on the payload precisely so the
 * denominator is self-describing when the two are compared.
 *
 * ⚠️ QF's flat threshold is `|change| ≤ 0.05%` — a judgment call with no ground
 * truth, and they say so. Flat share is a market-state variable, not a
 * constant: they measured 6.79% flat one session against 8.55% and 3.95% on the
 * two before it. **Validate side by side at the same instant**, never against a
 * remembered number, or you are fitting a constant to one observation of a
 * moving quantity.
 *
 * ### The original inclusion rules, kept as the specification
 *
 * No longer executed here, and no longer exactly what the server applies (see
 * above). Retained because they were derived by measurement rather than chosen,
 * and they are the reference any future reconciliation argues against.
 *
 * There is no correct answer to "how many names are in the market" — it is a
 * filter choice, and every venue picks a different one. These land at ~6,560
 * names, within 4% of what Webull shows.
 *
 *  • **Price ≥ $1.** Sub-dollar names move 20% on no news and would dominate
 *    the outer bands.
 *  • **Market cap ≥ $10M.** Below this a name is not really tradeable.
 *  • **Volume > 0.** Untraded today is not a flat day; it is no session.
 *  • **Common shares only.** ⚠️ A market-cap floor does NOT remove warrants,
 *    rights, units and preferreds — the trap is that FMP assigns the
 *    *underlying company's* market cap to them, so Fifth Third's preferred
 *    carries Fifth Third's $50B and sails through any threshold. They are also
 *    duplicates: `FITB-PA` and `FITB` are one company counted twice. NASDAQ
 *    encodes type in a 5th letter (`U`nits, `W`arrants, `R`ights); NYSE/AMEX
 *    use a separator (`-WT`, `-UN`, `-P*`). Any rule must be narrow enough to
 *    keep GOOGL, CMCSA, FWONK, BELFB, BRK-A and BRK-B — matching a bare
 *    trailing letter deletes PLTR as a "right" and SNOW as a "warrant".
 *  • **Deduplicated by symbol.** A name listed on two exchanges is one name.
 *
 * Percent change is `change / (price - change)` when the source carries only
 * an absolute move; a previous close of zero or less is bad data, not a 100%
 * move.
 */

/** Band edges as absolute percent. The last is open-ended. */
export interface BreadthBand {
  /** e.g. "0-2%", "2-5%", "5-10%", ">10%". */
  label: string;
  /** Names that fell by an amount inside this band. */
  down: number;
  /** Names that rose by an amount inside this band. */
  up: number;
}

export interface MarketBreadthData {
  /** Session date in EASTERN terms, YYYY-MM-DD. */
  date: string;
  bands: BreadthBand[];
  /** Names that closed exactly unchanged. */
  unchanged: number;
  /** Names counted. Shown in the tooltip and useful for spotting a bad session. */
  universe: number;
  /**
   * The instant these counts describe, ISO.
   *
   * Stated in the panel because breadth goes stale silently — the numbers look
   * exactly as plausible an hour after the close as they did during the
   * session, and a reader has no way to tell without being told.
   *
   * ⚠️ Must be the SESSION's instant, not the time the request was served. A
   * load at 17:16 is showing the 16:00 close and must say so. The date and the
   * time must come from the same clock, so that pre-market they are Monday's
   * date and Monday's close — never Tuesday's date against Monday's numbers.
   */
  capturedAt: string;

  /* ── derived, and deliberately unused ─────────────────────────────────
     QF computes these from the nine integers above and sends them along.
     The panel keeps deriving its own, because it needs `advancers` and
     `decliners` from the bands anyway to draw the tornado — taking the
     ratio from here while the totals stay locally summed would let the
     header disagree with the rows beneath it if the two denominators ever
     diverged. Declared so the wire is documented and so a future
     reconciliation has something to compare against.                     */

  /** advancers / decliners. `null`, not Infinity, when decliners === 0. */
  ad_ratio?: number | null;
  /** Shares of `universe`; the three sum to 100. */
  pct_up?: number;
  pct_down?: number;
  pct_flat?: number;
}

/* Lives under /v1/steamroom/* because the route is gated on the per-user
   Steamroom bearer this client already sends. The old ungated-name
   /v1/tape-breadth was mounted on QF's service-account gate, which accepts only
   the static service token — so every call from here 401'd. QF PR #869. */
const BREADTH_PATH = '/v1/steamroom/tape-breadth';

/**
 * Today's breadth across the tape.
 *
 * Throws rather than returning zeroes: an empty session and a failed fetch
 * look identical once they are counts, and one of them is a lie.
 *
 * ⚠️ `bands` is always length 4 in fixed order — "0-2%", "2-5%", "5-10%",
 * ">10%" — even when a count is zero, so it is safe to index positionally. The
 * panel still renders whatever length it is given.
 *
 * ⚠️ `unchanged` is EXCLUDED from the band up/down counts and INCLUDED in
 * `universe`. The three shares therefore sum to 100 only because flat is
 * counted in the denominator; summing the bands alone will not reach
 * `universe`.
 */
export async function fetchMarketBreadth(): Promise<MarketBreadthData> {
  return qfRestGet<MarketBreadthData>(BREADTH_PATH);
}
